import { formatDate } from '@angular/common';

import { type ChatMemberInfo, type ChatMessage, type ChatMessageId } from '../chat.models';

import { getChatMessageTime } from './get-chat-message-time';
import { isSameDay } from './is-same-day';

export interface ChatMessageViewModel {
  message: ChatMessage;
  index: number;
  showNewMessagesSeparatorAtStart: boolean;
  showNewMessagesSeparatorAtEnd: boolean;
  isCurrentUserAuthor: boolean;
  timeAgo: string;
  showTimeAgo: boolean;
  time: string;
  modifiedAgo?: string;
  showSender: boolean;
  showAuthorsImage: boolean;
  separateFromPrevious: boolean;
  isSearchResultSelected: boolean;
  isMessageEditInProgress: boolean;
  colorInfo?: { foreground: string; background: string };
  userStatus?: ChatMemberInfo;
  userStatusColor?: string;
}

interface BuildChatMessageParams {
  messages: ChatMessage[];
  lastReadId: ChatMessageId | undefined;
  allRead: boolean;
  scrollToId: ChatMessageId | undefined;
  membersColors: Record<string, { foreground: string; background: string }>;
  currentUserEmail: string | undefined;
  editId: ChatMessageId | undefined;
  chatMembersStatuses: Map<string, ChatMemberInfo> | undefined;
  locale: string;
  splitLayout: boolean;
}

export function buildChatMessagesVm({
  messages,
  lastReadId,
  allRead,
  scrollToId,
  membersColors,
  currentUserEmail,
  editId,
  chatMembersStatuses,
  locale,
  splitLayout,
}: BuildChatMessageParams): ChatMessageViewModel[] {
  return messages.map((message, index) => {
    const first = index === 0;
    const isPropConfigMessage = message.sender.id === 'prop-config';
    const prev = messages[index - 1];
    const next = messages[index + 1];
    const last = index === messages.length - 1;
    const timeAgo = getChatMessageTime(message.createdAt);
    const prevTimeAgo = prev ? getChatMessageTime(prev.createdAt) : -1;
    const isCurrentUserAuthor = message.sender.email === currentUserEmail;
    const isSenderRunStart =
      (first || prev?.sender.email !== message.sender.email) && !isPropConfigMessage;
    const showSender = isSenderRunStart && !(splitLayout && isCurrentUserAuthor);
    const showTimeAgo =
      (isSenderRunStart || prevTimeAgo !== timeAgo || !!message.modifiedAt) && !isPropConfigMessage;
    const showAuthorsImage =
      (last || next?.sender.email !== message.sender.email) && !isPropConfigMessage;
    const separateFromPrevious =
      first ||
      prev?.sender.email !== message.sender.email ||
      !isSameDay(prev?.createdAt, message.createdAt);
    const showNewMessagesSeparatorAtStart = !allRead && lastReadId === 0 && first;
    const showNewMessagesSeparatorAtEnd = !allRead && lastReadId === message.id && !last;
    const isMessageEditInProgress = message.id === editId;
    const editMode = !!editId && !isMessageEditInProgress;
    const memberStatus = chatMembersStatuses?.get(message.sender.email || '');

    return {
      message,
      index,

      showNewMessagesSeparatorAtStart,
      showNewMessagesSeparatorAtEnd,
      isCurrentUserAuthor,

      timeAgo,
      showTimeAgo,
      time: formatDate(message.createdAt, 'short', locale) || '',
      modifiedAgo: getChatMessageTime(message.modifiedAt),

      showSender: showSender,
      showAuthorsImage: showAuthorsImage,
      separateFromPrevious,

      isSearchResultSelected: scrollToId === message.id,
      isMessageEditInProgress,
      editMode,

      colorInfo: message.sender.email ? membersColors[message.sender.email] : undefined,
      userStatus: memberStatus,
    };
  });
}
